import { ethers } from 'ethers';
import { useEffect, useState } from 'react';
import { usePrivy } from '@privy-io/react-auth';
import { config } from '../shared/constants/WagmiConfig';
import { readContract } from 'wagmi/actions';
import { rewardABI } from '../assets/abis/rewardFactory';
import { useWriteContract } from 'wagmi';
import { waitForTransactionReceipt } from 'wagmi/actions';
import { enqueueSnackbar } from 'notistack';
import { renderContractError } from '../shared/utils/RenderContractError';
import { getClaimableRewardData } from '../api/contribution.api';
import { useQuery } from '@tanstack/react-query';
import { activeChain } from '../shared/utils/CheckNetwork';

const useClaimableReward = () => {
  const [claimableReward, setClaimableReward] = useState<bigint>(BigInt(0));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [virtualIds, setVirtualIds] = useState<bigint[]>([]);
  const [datasetIds, setDatasetIds] = useState<bigint[]>([]);
  const [modelNftIds, setModelNftIds] = useState<bigint[]>([]);
  const { user } = usePrivy();
  const { writeContractAsync } = useWriteContract();

  const fetchClaimableReward = async () => {
    if (user?.wallet?.address) {
      const claimableRewardData = await getClaimableRewardData(true);

      if (claimableRewardData) {
        const {
          virtualIds,
          datasetNftIds,
          modelNftIds,
        }: {
          virtualIds: bigint[];
          datasetNftIds: bigint[];
          modelNftIds: bigint[];
        } = claimableRewardData;
        setVirtualIds(virtualIds);
        setDatasetIds(datasetNftIds);
        setModelNftIds(modelNftIds);

        await readContract(config, {
          chainId: activeChain.id,
          abi: rewardABI,
          address: process.env.REACT_APP_REWARD_ADDRESS as `0x${string}`,
          functionName: 'getTotalClaimableRewards',
          args: [
            user?.wallet?.address as `0x${string}`,
            virtualIds,
            [...datasetNftIds, ...modelNftIds],
          ],
        })
          .then(async (response) => {
            let amount = response as bigint;
            setClaimableReward(amount);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  };
  const triggerClaimRewards = async () => {
    setIsLoading(true);

    let contributionIds = [...datasetIds, ...modelNftIds];
    await writeContractAsync({
      chainId: activeChain.id,
      abi: rewardABI,
      address: process.env.REACT_APP_REWARD_ADDRESS as `0x${string}`,
      functionName: 'claimAllRewards',
      args: [virtualIds, contributionIds],
    })
      .then(async (txHash) => {
        await waitForTransactionReceipt(config, {
          chainId: activeChain.id,
          hash: txHash,
        });

        await fetchClaimableReward();

        enqueueSnackbar({
          message: 'Succesfully claim $VIRTUALs',
          variant: 'success',
        });
      })
      .catch((err) => {
        const errMessage = renderContractError(err);

        if (!errMessage) {
          enqueueSnackbar({
            message: 'Failed to trigger claim rewards',
            variant: 'error',
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (user?.wallet?.address) {
      const getClaimableReward = async () => {
        await fetchClaimableReward();
      };

      getClaimableReward();
    }
  }, [user?.wallet?.address]);

  return {
    claimableReward,
    isLoading,
    triggerClaimRewards,
    fetchClaimableReward,
  };
};

export default useClaimableReward;
